import React, {
  useEffect,
  useState,
  useCallback
} from "react";
import uiConfig from "../config/uiConfig";

import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";

import ProjectTable from "../components/projects/ProjectTable";
import ProjectDialog from "../components/projects/ProjectDialog";
import DeleteProjectDialog from "../components/projects/DeleteProjectDialog";

import {
  getProjects,
  createProject,
  updateProject,
  deleteProject
} from "../services/projectService";
import { useUserRole } from "../context/UserRoleContext";
import { USER_ROLES } from "../auth/roles";

function Projects() {
  const { role, employee } = useUserRole();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);

  const [selectedProject, setSelectedProject] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const assignedTo = role === USER_ROLES.MANAGER ? (employee?.employee_id ?? -1) : null;
      const data = await getProjects(search, assignedTo);
      setProjects(data);

      setSelectedProjectIds((prev) => {
        const validIds = new Set(data.map((project) => project.project_id));
        return prev.filter((id) => validIds.has(id));
      });
    }
    catch (error) {
      console.error("Project Load Error:", error);
      setSnackbar({
        open: true,
        message: "Failed to load projects",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  }, [search, role, employee]);
useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  
  const handleSave = async (data) => {
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);

      const payload = { ...data };

      if (selectedProject) {
        await updateProject(selectedProject.project_id, payload);
      }
      else {
        await createProject(payload);
      }

      setDialogOpen(false);
      setSelectedProject(null);

      await loadProjects();

      setSnackbar({
        open: true,
        message: "Project saved successfully",
        severity: "success"
      });
    }
    catch (error) {
      console.error("Project Save Error:", error.response?.data || error);
      setSnackbar({
        open: true,
        message: "Operation failed",
        severity: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProject(selectedProject.project_id);
      await loadProjects();

      setDeleteOpen(false);
      setSelectedProject(null);

      setSnackbar({
        open: true,
        message: "Project deleted successfully",
        severity: "success"
      });
    }
    catch (error) {
      console.error("Project Delete Error:", error.response?.data || error);
      setSnackbar({
        open: true,
        message: "Delete failed",
        severity: "error"
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProjectIds.length === 0 || isBulkDeleting) {
      return;
    }

    try {
      setIsBulkDeleting(true);

      let successCount = 0;
      let failedCount = 0;

      for (const projectId of selectedProjectIds) {
        try {
          await deleteProject(projectId);
          successCount += 1;
        } catch (error) {
          failedCount += 1;
        }
      }

      setBulkDeleteOpen(false);
      await loadProjects();
      setSelectedProjectIds([]);

      if (failedCount === 0) {
        setSnackbar({
          open: true,
          message: `${successCount} project(s) deleted successfully`,
          severity: "success"
        });
      } else {
        setSnackbar({
          open: true,
          message: `${successCount} deleted, ${failedCount} failed`,
          severity: "warning"
        });
      }
    } catch (error) {
      console.error(error);
      setBulkDeleteOpen(false);
      setSnackbar({
        open: true,
        message: "Bulk delete failed",
        severity: "error"
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        background: "#f8fafc",
        minHeight: "100vh",
        p: 3
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight={600}
            color="#0f172a"
          >
            Projects
          </Typography>

          <Typography
            sx={{
              fontSize: 13,
              color: "#64748b"
            }}
          >
            Create projects and assign them to employees.
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.2}>
          <Button
            variant="outlined"
            color="error"
            startIcon={
              isBulkDeleting
                ? <CircularProgress size={16} color="inherit" />
                : <DeleteSweepIcon />
            }
            onClick={() => setBulkDeleteOpen(true)}
            disabled={selectedProjectIds.length === 0 || isBulkDeleting}
            sx={{
              height: 38,
              textTransform: "none",
              borderRadius: 2,
              px: 2,
            }}
          >
            {isBulkDeleting ? "Deleting..." : `Delete All (${selectedProjectIds.length})`}
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedProject(null);
              setDialogOpen(true);
            }}
            sx={{
              height: 38,
              textTransform: "none",
              background: uiConfig.buttonColor,
              borderRadius: 2,
              px: 3,
              boxShadow: "none",
              "&:hover": {
                background: "#1d4ed8",
                boxShadow: "none"
              }
            }}
          >
            Create Project
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          mb: 2,
          maxWidth: 320
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search projects"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
      </Box>

      <ProjectTable
        projects={projects}
        loading={loading}
        selectedIds={selectedProjectIds}
        onSelectionChange={setSelectedProjectIds}
        onEdit={(project) => {
          setSelectedProject(project);
          setDialogOpen(true);
        }}
        onDelete={(project) => {
          setSelectedProject(project);
          setDeleteOpen(true);
        }}
      />

      <ProjectDialog
        open={dialogOpen}
        project={selectedProject}
        isSaving={isSaving}
        onClose={() => {
          if (isSaving) {
            return;
          }
          setDialogOpen(false);
          setSelectedProject(null);
        }}
        onSave={handleSave}
      />

      <DeleteProjectDialog
        open={deleteOpen}
        project={selectedProject}
        onClose={() => {
          setDeleteOpen(false);
          setSelectedProject(null);
        }}
        onConfirm={handleDelete}
      />
      <Dialog
        open={bulkDeleteOpen}
        onClose={() => {
          if (isBulkDeleting) {
            return;
          }
          setBulkDeleteOpen(false);
        }}
        disableEscapeKeyDown={isBulkDeleting}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Selected Projects</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all selected projects?
            <br />
            <br />
            Selected: <strong>{selectedProjectIds.length}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setBulkDeleteOpen(false)}
            disabled={isBulkDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            disabled={isBulkDeleting}
            startIcon={
              isBulkDeleting
                ? <CircularProgress size={16} color="inherit" />
                : <DeleteSweepIcon />
            }
          >
            {isBulkDeleting ? "Deleting..." : "Delete All"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Projects;
